import { IconButton } from '@ads/components-react';

import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RoleViewModel } from '@/shared/api/generated/types';
import { DataTable } from '@/shared/ui/organisms/DataTable';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export interface RoleTableProps {
  readonly roles: RoleViewModel[];
  readonly isLoading?: boolean;
}

const columnHelper = createColumnHelper<RoleViewModel>();

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Organism RoleTable — tabela de roles com navegação para permissões.
 *
 * Clicando em qualquer linha navega para `/access-management/:roleId`.
 *
 * @example
 * ```tsx
 * <RoleTable roles={roles} isLoading={isLoading} />
 * ```
 */
export function RoleTable({ roles, isLoading }: RoleTableProps) {
  const { t } = useTranslation('accessControl');
  const navigate = useNavigate();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('roleName', {
          header: () => t('roles.table.columns.name'),
          cell: info => (
            <span className="ts-paragraph-xxs text-ctx-content-base">{info.getValue() ?? '—'}</span>
          ),
        }),
        columnHelper.display({
          id: 'actions',
          header: () => t('roles.table.columns.actions'),
          cell: ({ row }) => (
            <IconButton
              icon={Cog6ToothIcon}
              hierarchy="tertiary"
              size="md"
              ariaLabel={t('roles.table.actions.managePermissions')}
              onClick={() => {
                void navigate({
                  to: '/access-management/$roleId',
                  params: { roleId: String(row.original.roleId ?? 0) },
                });
              }}
            />
          ),
          size: 80,
        }),
      ] as ColumnDef<RoleViewModel, unknown>[],
    [t, navigate],
  );

  return (
    <DataTable
      data={roles}
      columns={columns}
      isLoading={isLoading ?? false}
      emptyStateMessage={t('roles.table.empty')}
      onRowClick={row =>
        void navigate({
          to: '/access-management/$roleId',
          params: { roleId: String(row.original.roleId ?? 0) },
        })
      }
    />
  );
}
